function _type_of(obj) {
    "@swc/helpers - typeof";
    return obj && typeof Symbol !== "undefined" && obj.constructor === Symbol ? "symbol" : typeof obj;
}
import * as React from 'react';
import { useRtl } from '@mui/system/RtlProvider';
import { useGridLogger } from "../../utils/useGridLogger.js";
import { gridColumnPositionsSelector, gridVisibleColumnDefinitionsSelector } from "../columns/gridColumnsSelector.js";
import { useGridSelector } from "../../utils/useGridSelector.js";
import { gridPageSelector, gridPageSizeSelector } from "../pagination/gridPaginationSelector.js";
import { gridRowCountSelector } from "../rows/gridRowsSelector.js";
import { gridRowsMetaSelector } from "../rows/gridRowsMetaSelector.js";
import { useGridApiMethod } from "../../utils/useGridApiMethod.js";
import { gridExpandedSortedRowEntriesSelector } from "../filter/gridFilterSelector.js";
import { gridDimensionsSelector } from "../dimensions/index.js";
import { gridListColumnSelector } from "../listView/gridListViewSelectors.js";
// Logic copied from https://www.w3.org/TR/wai-aria-practices/examples/listbox/js/listbox.js
// Similar to https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
function scrollIntoView(dimensions) {
    var containerSize = dimensions.containerSize, scrollPosition = dimensions.scrollPosition, elementSize = dimensions.elementSize, elementOffset = dimensions.elementOffset;
    var elementEnd = elementOffset + elementSize;
    // Always scroll to top when cell is higher than viewport to avoid scroll jump
    // See https://github.com/mui/mui-x/issues/4513 and https://github.com/mui/mui-x/issues/4514
    if (elementSize > containerSize) {
        return elementOffset;
    }
    if (elementEnd - containerSize > scrollPosition) {
        return elementEnd - containerSize;
    }
    if (elementOffset < scrollPosition) {
        return elementOffset;
    }
    return undefined;
}
/**
 * @requires useGridPagination (state) - can be after, async only
 * @requires useGridColumns (state) - can be after, async only
 * @requires useGridRows (state) - can be after, async only
 * @requires useGridRowsMeta (state) - can be after, async only
 * @requires useGridFilter (state)
 * @requires useGridColumnSpanning (method)
 */ export var useGridScroll = function(apiRef, props) {
    var isRtl = useRtl();
    var logger = useGridLogger(apiRef, 'useGridScroll');
    var colRef = apiRef.current.columnHeadersContainerRef;
    var virtualScrollerRef = apiRef.current.virtualScrollerRef;
    var visibleSortedRows = useGridSelector(apiRef, gridExpandedSortedRowEntriesSelector);
    var scrollToIndexes = React.useCallback(function(params) {
        var dimensions = gridDimensionsSelector(apiRef.current.state);
        var totalRowCount = gridRowCountSelector(apiRef);
        var visibleColumns = props.unstable_listView ? [
            gridListColumnSelector(apiRef.current.state)
        ] : gridVisibleColumnDefinitionsSelector(apiRef);
        var scrollToHeader = params.rowIndex == null;
        if (!scrollToHeader && totalRowCount === 0 || visibleColumns.length === 0) {
            return false;
        }
        logger.debug("Scrolling to cell at row ".concat(params.rowIndex, ", col: ").concat(params.colIndex, " "));
        var scrollCoordinates = {};
        if (params.colIndex !== undefined) {
            var columnPositions = gridColumnPositionsSelector(apiRef);
            var cellWidth;
            if (typeof params.rowIndex !== 'undefined') {
                var _visibleSortedRows_params_rowIndex;
                var rowId = (_visibleSortedRows_params_rowIndex = visibleSortedRows[params.rowIndex]) === null || _visibleSortedRows_params_rowIndex === void 0 ? void 0 : _visibleSortedRows_params_rowIndex.id;
                var cellColSpanInfo = apiRef.current.unstable_getCellColSpanInfo(rowId, params.colIndex);
                if (cellColSpanInfo && !cellColSpanInfo.spannedByColSpan) {
                    cellWidth = cellColSpanInfo.cellProps.width;
                }
            }
            if (typeof cellWidth === 'undefined') {
                cellWidth = visibleColumns[params.colIndex].computedWidth;
            }
            // When using RTL, `scrollLeft` becomes negative, so we must ensure that we only compare values.
            scrollCoordinates.left = scrollIntoView({
                containerSize: dimensions.viewportOuterSize.width,
                scrollPosition: Math.abs(virtualScrollerRef.current.scrollLeft),
                elementSize: cellWidth,
                elementOffset: columnPositions[params.colIndex]
            });
        }
        if (params.rowIndex !== undefined) {
            var rowsMeta = gridRowsMetaSelector(apiRef.current.state);
            var page = gridPageSelector(apiRef);
            var pageSize = gridPageSizeSelector(apiRef);
            var elementIndex = !props.pagination ? params.rowIndex : params.rowIndex - page * pageSize;
            var targetOffsetHeight = rowsMeta.positions[elementIndex + 1] ? rowsMeta.positions[elementIndex + 1] - rowsMeta.positions[elementIndex] : rowsMeta.currentPageTotalHeight - rowsMeta.positions[elementIndex];
            scrollCoordinates.top = scrollIntoView({
                containerSize: dimensions.viewportInnerSize.height,
                scrollPosition: virtualScrollerRef.current.scrollTop,
                elementSize: targetOffsetHeight,
                elementOffset: rowsMeta.positions[elementIndex]
            });
        }
        scrollCoordinates = apiRef.current.unstable_applyPipeProcessors('scrollToIndexes', scrollCoordinates, params);
        if (_type_of(scrollCoordinates.left) !== undefined || _type_of(scrollCoordinates.top) !== undefined) {
            apiRef.current.scroll(scrollCoordinates);
            return true;
        }
        return false;
    }, [
        logger,
        apiRef,
        virtualScrollerRef,
        props.pagination,
        visibleSortedRows,
        props.unstable_listView
    ]);
    var scroll = React.useCallback(function(params) {
        if (virtualScrollerRef.current && params.left !== undefined && colRef.current) {
            var direction = isRtl ? -1 : 1;
            colRef.current.scrollLeft = params.left;
            virtualScrollerRef.current.scrollLeft = direction * params.left;
            logger.debug("Scrolling left: ".concat(params.left));
        }
        if (virtualScrollerRef.current && params.top !== undefined) {
            virtualScrollerRef.current.scrollTop = params.top;
            logger.debug("Scrolling top: ".concat(params.top));
        }
        logger.debug("Scrolling, updating container, and viewport");
    }, [
        virtualScrollerRef,
        isRtl,
        colRef,
        logger
    ]);
    var getScrollPosition = React.useCallback(function() {
        if (!(virtualScrollerRef === null || virtualScrollerRef === void 0 ? void 0 : virtualScrollerRef.current)) {
            return {
                top: 0,
                left: 0
            };
        }
        return {
            top: virtualScrollerRef.current.scrollTop,
            left: virtualScrollerRef.current.scrollLeft
        };
    }, [
        virtualScrollerRef
    ]);
    var scrollApi = {
        scroll: scroll,
        scrollToIndexes: scrollToIndexes,
        getScrollPosition: getScrollPosition
    };
    useGridApiMethod(apiRef, scrollApi, 'public');
};
